import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { AiBadge } from '@/components/ai/AiBadge';
import { runAi } from '@/lib/ai';
import { assistantDemo } from '@/lib/assistantDemo';
import { initialLoadings, getCarrier } from '@/data/arrivalsData';
import { subViews } from './subViews';
import { t, getLanguage } from '@/i18n';
import { ArrowLeft, Check, Loader2, Sparkles } from 'lucide-react';

const pad = (n) => String(n).padStart(2, '0');
const hhmm = (iso) => { const d = new Date(iso); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };

// Where an action leads; the app opens nothing without the user's click.
export const actionPath = (a) =>
  a.type === 'open-loading' ? `/kamiony?nakladka=${encodeURIComponent(a.loadingId)}`
    : a.type === 'follow-up' ? '/kamiony?kontrola=1'
      : a.path;

// Assistant answer inside Ctrl+K (#76): answer + one action to confirm.
export const AssistantPanel = ({ query, pages, onGo, onBack }) => {
  const [reply, setReply] = useState(null);

  useEffect(() => {
    let alive = true;
    const routes = [
      ...pages.map(p => ({ path: p.href, label: p.label })),
      ...Object.values(subViews).flat().map(v => ({ path: v.path, label: t(v.label) })),
    ];
    const loadings = initialLoadings.filter(l => l.state !== 'loaded').map(l => ({
      id: l.id, plate: l.plate, carrier: getCarrier(l.carrierId)?.name || '—', state: l.state, plannedAt: hhmm(l.plannedAt),
    }));
    runAi('assistant', { query, lang: getLanguage(), loadings, routes: routes.slice(0, 40) }, assistantDemo)
      .then(res => alive && setReply(res));
    return () => { alive = false; };
  }, [query, pages]);

  return (
    <div className="p-4 space-y-3" aria-live="polite">
      <div className="flex items-center gap-2 text-xs font-semibold text-primary">
        <Sparkles className="h-3.5 w-3.5" /> {t('ai.assistant.title')}
        {reply && <AiBadge mode={reply.mode} className="ml-auto" />}
      </div>
      <div className="text-xs text-muted-foreground">„{query}“</div>
      {!reply ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('ai.assistant.thinking')}</div>
      ) : (
        <>
          <p className="text-sm">{reply.result.answer}</p>
          <div className="flex flex-wrap gap-2">
            {reply.result.action && (
              <Button size="sm" className="gap-1" onClick={() => onGo(actionPath(reply.result.action))}>
                <Check className="h-3.5 w-3.5" /> {reply.result.action.label}
              </Button>
            )}
            <Button size="sm" variant="ghost" className="gap-1" onClick={onBack}>
              <ArrowLeft className="h-3.5 w-3.5" /> {t('ai.assistant.back')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
